import { Button, Form, Input, Modal, Select, Space, Switch, Table, Tag, message } from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { QuotationTemplate, QuotationTemplateType } from "src/common/models/quotation";
import { getInputRequiredRule } from "src/common/util";
import { serviceLoading } from "src/common/apiLoader/state/selection";
import { RootState } from "src/state/reducer";
import { getQuotationTemplatesApi, upsertQuotationTemplateApi } from "../state/action";
import { GET_QUOTATION_TEMPLATES_API, UPSERT_QUOTATION_TEMPLATE_API } from "../state/actionTypes";

interface ReduxProps {
    templates?: QuotationTemplate[]
    isLoading: boolean
    isSaving: boolean
    saveSuccess: boolean
}

interface TemplateFormValues {
    id?: number
    name: string
    description?: string
    type: QuotationTemplateType
    htmlTemplate: string
    htmlDetailTemplate?: string
    htmlPolicyTemplate?: string
    backgroundImageUrl?: string
    isActive: boolean
    salutation?: string
    body?: string
    close?: string
    brandName?: string
    brandPhone?: string
    brandWebsite?: string
}

export const QuotationTemplates = () => {
    const dispatch = useDispatch()
    const [form] = Form.useForm<TemplateFormValues>()
    const [modalOpen, setModalOpen] = useState<boolean>(false)
    const [editingTemplate, setEditingTemplate] = useState<QuotationTemplate | undefined>(undefined)

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        templates: state.quotation.quotationTemplates,
        isLoading: serviceLoading(state, [GET_QUOTATION_TEMPLATES_API]),
        isSaving: serviceLoading(state, [UPSERT_QUOTATION_TEMPLATE_API]),
        saveSuccess: state.quotation.upsertQuotationTemplateSuccess,
    }))

    useEffect(() => {
        dispatch(getQuotationTemplatesApi(false))
    }, [dispatch])

    useEffect(() => {
        if (reduxProps.saveSuccess) {
            message.success("Plantilla guardada")
            setModalOpen(false)
            setEditingTemplate(undefined)
            form.resetFields()
        }
    }, [reduxProps.saveSuccess, form])

    const openCreateModal = () => {
        setEditingTemplate(undefined)
        form.setFieldsValue({
            type: QuotationTemplateType.QUOTATION,
            isActive: true,
        })
        setModalOpen(true)
    }

    const openEditModal = (template: QuotationTemplate) => {
        setEditingTemplate(template)
        form.setFieldsValue({
            ...template,
            salutation: template.defaultData?.salutation,
            body: template.defaultData?.body,
            close: template.defaultData?.close,
            brandName: template.defaultData?.brandName,
            brandPhone: template.defaultData?.brandPhone,
            brandWebsite: template.defaultData?.brandWebsite,
        })
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setEditingTemplate(undefined)
        form.resetFields()
    }

    const handleSave = async () => {
        const values = await form.validateFields()
        dispatch(upsertQuotationTemplateApi({
            id: editingTemplate?.id,
            name: values.name,
            description: values.description,
            type: values.type,
            htmlTemplate: values.htmlTemplate,
            htmlDetailTemplate: values.htmlDetailTemplate,
            htmlPolicyTemplate: values.htmlPolicyTemplate,
            backgroundImageUrl: values.backgroundImageUrl,
            isActive: values.isActive,
            defaultData: {
                salutation: values.salutation,
                body: values.body,
                close: values.close,
                brandName: values.brandName,
                brandPhone: values.brandPhone,
                brandWebsite: values.brandWebsite,
            },
        }))
    }

    const columns = [
        {
            title: "Nombre",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Tipo",
            dataIndex: "type",
            key: "type",
            render: (type: QuotationTemplateType) => <Tag color={type === QuotationTemplateType.PROPOSAL ? "geekblue" : "green"}>{type}</Tag>,
        },
        {
            title: "Estado",
            dataIndex: "isActive",
            key: "isActive",
            render: (isActive: boolean) => <Tag color={isActive ? "green" : "red"}>{isActive ? "Activa" : "Inactiva"}</Tag>,
        },
        {
            title: "Acciones",
            key: "actions",
            align: "right" as const,
            render: (_: any, template: QuotationTemplate) => (
                <Button size="small" onClick={() => openEditModal(template)}>
                    Editar
                </Button>
            ),
        },
    ]

    return (
        <>
            <Space style={{ width: "100%", justifyContent: "space-between", marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Plantillas de Cotización</h3>
                <Button type="primary" onClick={openCreateModal}>
                    Nueva plantilla
                </Button>
            </Space>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={reduxProps.templates || []}
                loading={reduxProps.isLoading}
                pagination={false}
                size="small"
            />

            <Modal
                title={editingTemplate ? "Editar plantilla" : "Nueva plantilla"}
                open={modalOpen}
                onCancel={closeModal}
                onOk={handleSave}
                okText="Guardar"
                cancelText="Cancelar"
                confirmLoading={reduxProps.isSaving}
                width={920}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    size="small"
                    requiredMark={false}
                    initialValues={{ isActive: true, type: QuotationTemplateType.QUOTATION }}
                >
                    <Space direction="vertical" style={{ width: "100%" }} size={0}>
                        <Form.Item label="Nombre" name="name" rules={[getInputRequiredRule("Nombre")]}>
                            <Input />
                        </Form.Item>
                        <Form.Item label="Descripcion" name="description">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Tipo" name="type" rules={[getInputRequiredRule("Tipo")]}>
                            <Select
                                options={[
                                    { value: QuotationTemplateType.QUOTATION, label: "Cotizacion" },
                                    { value: QuotationTemplateType.PROPOSAL, label: "Propuesta" },
                                ]}
                            />
                        </Form.Item>
                        <Form.Item label="Activa" name="isActive" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                        <Form.Item label="URL de fondo" name="backgroundImageUrl">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Saludo por defecto" name="salutation">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Cuerpo por defecto" name="body">
                            <Input.TextArea rows={2} />
                        </Form.Item>
                        <Form.Item label="Cierre por defecto" name="close">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Marca" name="brandName">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Telefono de marca" name="brandPhone">
                            <Input />
                        </Form.Item>
                        <Form.Item label="Sitio web de marca" name="brandWebsite">
                            <Input />
                        </Form.Item>
                        <Form.Item label="HTML principal" name="htmlTemplate" rules={[getInputRequiredRule("HTML principal")]}>
                            <Input.TextArea rows={8} />
                        </Form.Item>
                        <Form.Item label="HTML detalle" name="htmlDetailTemplate">
                            <Input.TextArea rows={5} />
                        </Form.Item>
                        <Form.Item label="HTML terminos" name="htmlPolicyTemplate">
                            <Input.TextArea rows={4} />
                        </Form.Item>
                    </Space>
                </Form>
            </Modal>
        </>
    )
}
